import React, { useState, useEffect } from "react";
import { useGetallusersQuery } from "../../redux/slices/allusers/allusersApi";
import { Header, ExportCSVButton, Pagination } from "../../components";
import SearchBox from "../../components/SearchBox";
import { useStateContext } from "../../contexts/ContextProvider";

const AllUsersPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500); 

    return () => clearTimeout(timer);
  }, [searchTerm]);

  
  const { isLoading, error, data } = useGetallusersQuery({
    page: currentPage,
    search: debouncedSearchTerm,
    sortField,
    sortDirection,
  });

  const { currentMode } = useStateContext();

  
  const allusers = data?.data || [];
  const totalRecords = data?.total || 0;
  const backendLimit = data?.limit || 10; 
  const totalPages = Math.ceil(totalRecords / backendLimit);

  
  const sortRecords = (records, field, direction) =>
    [...records].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];

      
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      
      if (field === "created_at") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      
      if (field === "first_name") {
        aValue = `${a.first_name || ""} ${a.last_name || ""}`
          .trim()
          .toLowerCase();
        bValue = `${b.first_name || ""} ${b.last_name || ""}`
          .trim()
          .toLowerCase();
      }

      
      if (
        typeof aValue === "string" &&
        aValue !== "" &&
        field !== "first_name"
      ) {
        aValue = aValue.toLowerCase();
      }
      if (
        typeof bValue === "string" &&
        bValue !== "" &&
        field !== "first_name"
      ) {
        bValue = bValue.toLowerCase();
      }

      if (direction === "asc") {
        if (aValue > bValue) return 1;
        if (aValue < bValue) return -1;
        return 0;
      }
      if (aValue < bValue) return 1;
      if (aValue > bValue) return -1;
      return 0;
    });

  
  const sortedRecords = sortRecords(allusers, sortField, sortDirection);

  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); 
  };

  
  const getSortIndicator = (field) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 ml-1 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }
    return sortDirection === "asc" ? (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 ml-1 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

 
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.trim());
    setCurrentPage(1); 
  };

  
  const renderVerificationStatus = (isVerified) => {
    const isVerifiedValue = Boolean(isVerified);
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          isVerifiedValue
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800"
        }`}
      >
        {isVerifiedValue ? "Yes" : "No"}
      </span>
    );
  };

  
  const renderRiskLevelBadge = (riskLevel) => {
    if (!riskLevel) {
      return (
        <span className="px-3 py-1 rounded-full font-semibold text-sm bg-gray-100 text-gray-500">
          Risk Level Not Found
        </span>
      );
    }

    let className = "px-3 py-1 rounded-full font-semibold text-sm";

    if (riskLevel === "Aggressive") {
      className += " bg-red-100 text-red-800";
    } else if (riskLevel === "Growth") {
      className += " bg-green-100 text-green-800";
    } else if (riskLevel === "Balanced") {
      className += " bg-orange-100 text-orange-800";
    } else {
      // Default styling for unknown risk levels
      className += " bg-gray-100 text-gray-800";
    }

    return <span className={className}>{riskLevel}</span>;
  };

  const getRowBackgroundClass = (index) => {
    if (currentMode === "Dark") {
      return index % 2 === 0
        ? "bg-secondary-dark-bg"
        : "bg-secondary-dark-bg-light";
    }
    return index % 2 === 0 ? "bg-white" : "bg-gray-50";
  };

  if (isLoading) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white"
        }`}
      >
        <Header category="Page" title="All Users" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
          currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white"
        }`}
      >
        <Header category="Page" title="All Users" />
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === "Dark"
                ? "bg-gray-800 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === "Dark" ? "text-white" : "text-gray-700"
              }`}
            >
              Unable to Load Users
            </h3>
            <p
              className={`text-sm mb-4 ${
                currentMode === "Dark" ? "text-gray-300" : "text-gray-500"
              }`}
            >
              We encountered an issue while loading the user data. Please try
              refreshing the page or contact support if the problem persists.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${
        currentMode === "Dark" ? "bg-secondary-dark-bg" : "bg-white shadow-lg"
      }`}
    >
      <Header category="Page" title="All Users" />
      {/* Search Box and Export Button */}
      <div className="mb-4 flex items-center justify-between">
        <SearchBox
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by Email"
        />
        <ExportCSVButton
          data={sortedRecords}
          filename="all-users.xlsx"
          buttonText="Export to Excel"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table
          className={`min-w-max border ${
            currentMode === "Dark"
              ? "bg-secondary-dark-bg border-gray-600"
              : "bg-white border-gray-300"
          }`}
        >
          <thead
            className={
              currentMode === "Dark"
                ? "bg-secondary-dark-bg-light"
                : "bg-gray-50"
            }
          >
            <tr>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("id")}
              >
                <div className="flex items-center">
                  User ID
                  {getSortIndicator("id")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("email")}
              >
                <div className="flex items-center">
                  Email
                  {getSortIndicator("email")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("first_name")}
              >
                <div className="flex items-center">
                  Name
                  {getSortIndicator("first_name")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("phone_number")}
              >
                <div className="flex items-center">
                  Phone Number
                  {getSortIndicator("phone_number")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("role")}
              >
                <div className="flex items-center">
                  Role
                  {getSortIndicator("role")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("risk_level")}
              >
                <div className="flex items-center">
                  Risk Level
                  {getSortIndicator("risk_level")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("is_email_verified")}
              >
                <div className="flex items-center">
                  Email Verified
                  {getSortIndicator("is_email_verified")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("is_phone_verified")}
              >
                <div className="flex items-center">
                  Phone Verified
                  {getSortIndicator("is_phone_verified")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("is_kyc_verified")}
              >
                <div className="flex items-center">
                  KYC Verified
                  {getSortIndicator("is_kyc_verified")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("is_risk_verified")}
              >
                <div className="flex items-center">
                  Risk Verified
                  {getSortIndicator("is_risk_verified")}
                </div>
              </th>
              <th
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${
                  currentMode === "Dark"
                    ? "text-white border-gray-600 hover:bg-gray-700"
                    : "text-gray-500 border-gray-300"
                }`}
                onClick={() => handleSort("created_at")}
              >
                <div className="flex items-center">
                  Created At
                  {getSortIndicator("created_at")}
                </div>
              </th>
            </tr>
          </thead>
          <tbody
            className={`divide-y ${
              currentMode === "Dark"
                ? "bg-secondary-dark-bg divide-gray-600"
                : "bg-white divide-gray-200"
            }`}
          >
            {sortedRecords.map((record, index) => (
              <tr key={record.id} className={`${getRowBackgroundClass(index)}`}>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    currentMode === "Dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {record.id}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.email}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {`${record.first_name || ""} ${
                    record.last_name || ""
                  }`.trim()}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.phone_number}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.role}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {renderRiskLevelBadge(record.risk_level)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {renderVerificationStatus(record.is_email_verified)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {renderVerificationStatus(record.is_phone_verified)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {renderVerificationStatus(record.is_kyc_verified)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {renderVerificationStatus(record.is_risk_verified)}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    currentMode === "Dark" ? "text-white" : "text-gray-500"
                  }`}
                >
                  {record.created_at
                    ? new Date(record.created_at).toLocaleString()
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalRecords={totalRecords}
        backendLimit={backendLimit}
        recordsCount={sortedRecords.length}
      />
      {/* No Records Message */}
      {sortedRecords.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div
            className={`text-center rounded-lg p-8 max-w-md mx-auto border ${
              currentMode === "Dark"
                ? "bg-gray-800 border-gray-600"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="mb-4">
              <svg
                className={`w-16 h-16 mx-auto ${
                  currentMode === "Dark" ? "text-gray-500" : "text-gray-400"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${
                currentMode === "Dark" ? "text-white" : "text-gray-700"
              }`}
            >
              No Users Found
            </h3>
            <p
              className={`text-sm ${
                currentMode === "Dark" ? "text-gray-300" : "text-gray-500"
              }`}
            >
              {searchTerm
                ? "No users match your search criteria. Try adjusting your search terms."
                : "There are currently no users available."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsersPage;
